import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Alert, DatePicker, Form, Input, InputNumber, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { i18n } from '../../../i18n/index.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { STAGE_ORDER } from '../lib/kanban.js';
import { DealLinkFields } from './DealLinkFields.js';
import { DealSourceSelect } from './DealSourceSelect.js';
import type { DealListItem, DealStage } from '../api/deals.client.js';
import { useCreateDealMutation } from '../hooks/useCreateDealMutation.js';
import { useUpdateDealMutation } from '../hooks/useDealMutations.js';
import './deal-detail.css';

export const DEAL_STAGE_OPTIONS = STAGE_ORDER.map((value) => ({
  value,
  get label() {
    return getEnumLabel('dealStage', value, i18n.t);
  },
}));
interface DealFormValues {
  title: string;
  amount?: number | null;
  source?: string;
  nextActivity?: Dayjs | null;
  companyId?: string | undefined;
  contactId?: string | undefined;
}
export type DealFormRecord = DealListItem & { source?: string | null };
interface DealFormModalProps {
  open: boolean;
  onClose: () => void;
  deal?: DealFormRecord | null;
  initialStage?: DealStage;
  lockedContactId?: string;
  lockedCompanyId?: string;
  focusNextStep?: boolean;
}
const signature = (values: DealFormValues): string =>
  JSON.stringify([
    values.title?.trim() ?? '',
    values.amount ?? 0,
    values.source?.trim() ?? '',
    values.nextActivity?.isValid() ? values.nextActivity.toISOString() : null,
    values.contactId ?? null,
    values.companyId ?? null,
  ]);
export function DealFormModal({
  open,
  onClose,
  deal,
  initialStage = 'New',
  lockedContactId,
  lockedCompanyId,
  focusNextStep = false,
}: DealFormModalProps): ReactElement {
  const { t, locale } = useTranslation();
  const [form] = Form.useForm<DealFormValues>();
  const createMut = useCreateDealMutation();
  const updateMut = useUpdateDealMutation();
  const [created, setCreated] = useState<DealListItem | null>(null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(false);
  const baseline = useRef('');
  const pending = createMut.isPending || updateMut.isPending;
  const error = createMut.error ?? updateMut.error;
  useEffect(() => {
    if (!open) return;
    createMut.reset();
    updateMut.reset();
    form.resetFields();
    setCreated(null);
    setConflict(false);
    const values: DealFormValues = {
      title: deal?.title ?? '',
      amount: deal ? Number(deal.amount) : null,
      source: deal?.source ?? '',
      nextActivity: deal?.nextActivityDue ? dayjs(deal.nextActivityDue) : null,
      companyId: lockedCompanyId ?? deal?.companyId ?? undefined,
      contactId: lockedContactId ?? deal?.contactId ?? undefined,
    };
    form.setFieldsValue(values);
    baseline.current = signature(values);
    setDirty(false);
  }, [open, deal, lockedCompanyId, lockedContactId, form]);
  const changed = (): void => setDirty(signature(form.getFieldsValue(true)) !== baseline.current);
  const save = async (values: DealFormValues): Promise<void> => {
    const contactId = lockedContactId ?? values.contactId;
    const companyId = lockedCompanyId ?? values.companyId;
    const input = {
      title: values.title.trim(),
      amount: values.amount ?? 0,
      source: values.source?.trim() || null,
      nextActivityDue: values.nextActivity?.isValid() ? values.nextActivity.toISOString() : null,
      contactId: contactId ?? null,
      companyId: companyId ?? null,
    };
    try {
      if (deal) {
        await updateMut.mutateAsync({
          id: deal.id,
          input: { ...input, ...(deal.updatedAt ? { expectedUpdatedAt: deal.updatedAt } : {}) },
        });
      } else {
        const record =
          created ??
          (await createMut.mutateAsync({
            title: input.title,
            amount: input.amount,
            stage: initialStage,
            ...(contactId ? { contactId } : {}),
            ...(companyId ? { companyId } : {}),
          }));
        setCreated(record);
        if (created || input.source || input.nextActivityDue)
          await updateMut.mutateAsync({
            id: record.id,
            input: {
              ...input,
              ...(record.updatedAt ? { expectedUpdatedAt: record.updatedAt } : {}),
            },
          });
      }
      onClose();
    } catch {
      /* Mutation errors are displayed inline; a partial create is retained for retry. */
    }
  };
  const blocked = pending || conflict || Boolean(deal && !dirty);
  return (
    <Modal
      open={open}
      title={t(
        deal
          ? 'crm.features.features.deals.components.dealformmodal.редактировать_сделку'
          : 'crm.features.features.deals.components.dealformmodal.новая_сделка',
      )}
      width={720}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('crm.entity_detail.save')}
      cancelText={t('crm.entity_detail.cancel')}
      okButtonProps={{ disabled: blocked }}
      confirmLoading={pending}
      destroyOnHidden
      closable={{ 'aria-label': t('crm.deal_card.close_chat') }}
      afterOpenChange={(visible) => {
        if (visible && focusNextStep)
          (form.getFieldInstance('nextActivity') as { focus?: () => void } | undefined)?.focus?.();
      }}
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={t(
            created
              ? 'crm.deal_detail.partial_create'
              : deal
                ? 'crm.features.features.deals.components.dealformmodal.не_удалось_сохранить_сделку'
                : 'crm.features.features.deals.components.dealformmodal.не_удалось_создать_сделку',
          )}
          description={error instanceof Error ? error.message : undefined}
        />
      ) : null}
      <Form
        form={form}
        layout="vertical"
        className="deal-form-grid"
        onValuesChange={changed}
        onFinish={(values) => {
          if (!blocked) void save(values);
        }}
        disabled={pending}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            if (!blocked) form.submit();
          }
        }}
      >
        <Form.Item
          name="title"
          className="deal-form-full"
          label={t('crm.features.features.deals.components.dealformmodal.название')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t(
                'crm.features.features.deals.components.dealformmodal.введите_название_сделки',
              ),
            },
          ]}
        >
          <Input maxLength={200} autoFocus={!focusNextStep} />
        </Form.Item>
        <Form.Item
          name="amount"
          className="deal-form-full"
          label={t('crm.deal_card.form.amount')}
          rules={[
            {
              type: 'number',
              min: 0,
              message: t(
                'crm.features.features.deals.components.dealformmodal.сумма_не_может_быть_отрицательной',
              ),
            },
          ]}
        >
          <InputNumber<number>
            className="w-full"
            min={0}
            addonAfter={
              new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
                style: 'currency',
                currency: deal?.currency ?? 'RUB',
                currencyDisplay: 'narrowSymbol',
              })
                .formatToParts(0)
                .find((part) => part.type === 'currency')?.value
            }
          />
        </Form.Item>
        <DealLinkFields
          form={form}
          {...(lockedContactId ? { lockedContactId } : {})}
          {...(lockedCompanyId ? { lockedCompanyId } : {})}
          onChange={changed}
          onConflict={setConflict}
        />
        <Form.Item name="source" className="deal-form-full" label={t('crm.deal_card.form.source')}>
          <DealSourceSelect />
        </Form.Item>
        <Form.Item
          name="nextActivity"
          className="deal-form-full"
          label={t('crm.deal_detail.next_step')}
        >
          <DatePicker
            showTime
            format={locale === 'ru' ? 'DD.MM.YYYY HH:mm' : 'DD/MM/YYYY HH:mm'}
            className="w-full"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
